import {
  compareDates,
  parseMrzDate,
  toSimpleDate
} from "./chunk-QW2F5SQ5.js";
import {
  DIGIT_FOR_LETTER,
  ICAO_SPECIAL_CODES,
  LAYOUTS,
  LETTERS_FOR_DIGIT,
  charValue,
  checkPasses,
  computeCheckDigit,
  correctLines,
  fieldDef,
  isKnownCountry,
  isValidCheckDigit,
  lookupCountry,
  readField,
  resolveCheck
} from "./chunk-YATZMKUB.js";

// src/length.ts
var ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<";
var MAX_CANDIDATES = 4e3;
function classScore(lines, layout) {
  let score = 0;
  for (const d of layout.fields) {
    const v = (lines[d.line] ?? "").slice(d.start, d.start + d.length);
    const allFiller = /^<+$/.test(v);
    for (const c of v) {
      if (d.cls === "digit" ? /[0-9]/.test(c) || allFiller : d.cls === "alpha" ? /[A-Z<]/.test(c) : true)
        score++;
    }
    if ((d.name === "birthDate" || d.name === "expiryDate") && /^\d{6}$/.test(v)) {
      const month = Number(v.slice(2, 4));
      const day = Number(v.slice(4, 6));
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) score += 10;
    }
  }
  return score;
}
function evaluate(lines, layout, maxK) {
  const out = correctLines(lines, layout, maxK);
  const grid = out.lines.map((l) => [...l]);
  let passing = 0;
  for (const check of layout.checks) {
    const rc = resolveCheck(layout, check, out.lines);
    if (rc && checkPasses(grid, rc)) passing++;
  }
  const changes = out.corrections.reduce((n, c) => n + c.changes.length, 0);
  return passing * 1e5 - changes * 1e3 + classScore(lines, layout);
}
function repairLineLengths(rawLines, fallback, layout, maxK = 2) {
  const lines = [...fallback];
  const warnings = [];
  const repaired = [];
  rawLines.forEach((raw, i) => {
    const diff = raw.length - layout.lineLength;
    if (diff === 0 || diff > 2 || diff < -1) return;
    const candidates = /* @__PURE__ */ new Map();
    const add = (c, bonus) => candidates.set(c, Math.max(bonus, candidates.get(c) ?? 0));
    const narrow = (c) => c && "I1L".includes(c) ? 1 : 0;
    if (diff > 0) {
      for (let a = 0; a < raw.length; a++) {
        const once = raw.slice(0, a) + raw.slice(a + 1);
        if (diff === 1) add(once, narrow(raw[a]));
        else
          for (let b = a; b < once.length; b++)
            add(once.slice(0, b) + once.slice(b + 1), narrow(raw[a]) + narrow(once[b]));
        if (candidates.size > MAX_CANDIDATES) break;
      }
    } else {
      for (let a = 0; a <= raw.length; a++) {
        for (const c of ALPHABET) add(raw.slice(0, a) + c + raw.slice(a), 0);
      }
    }
    const score = (candidate) => {
      const trial = [...lines];
      trial[i] = candidate;
      return evaluate(trial, layout, maxK);
    };
    const baseline = score(lines[i]);
    let best = baseline;
    let winners = [];
    for (const [c, bonus] of candidates) {
      const s = score(c) + bonus;
      if (s > best) {
        best = s;
        winners = [c];
      } else if (s === best && s > baseline) winners.push(c);
    }
    if (winners.length === 1 && Math.round(best / 1e5) > Math.round(baseline / 1e5)) {
      lines[i] = winners[0];
      repaired.push(i);
      warnings.push({
        code: "line_length_repaired",
        message: `Line ${i + 1} had ${raw.length} characters; ${diff > 0 ? `removed ${diff} extra` : "restored 1 missing"} character(s) where the check digits indicated (now "${winners[0]}").`
      });
    }
  });
  return { lines, warnings, repaired };
}

// src/names.ts
var clean = (s) => s.replace(/</g, " ").replace(/\s+/g, " ").trim();
function parseName(field) {
  const truncated = field.length > 0 && !field.endsWith("<");
  const body = field.replace(/<+$/, "");
  const sep = body.indexOf("<<");
  if (sep < 0) return { surname: clean(body), givenNames: "", truncated };
  return { surname: clean(body.slice(0, sep)), givenNames: clean(body.slice(sep + 2)), truncated };
}

// src/normalize.ts
var LOOKALIKES = {
  "\xAB": "<<",
  "\u2039": "<",
  "\uFF1C": "<",
  "\u3008": "<",
  "\u27E8": "<",
  "\u2018": "",
  "\u2019": ""
};
var MRZ_CHARS = /[A-Z0-9<]/g;
function mrzRatio(line) {
  return line.length ? (line.match(MRZ_CHARS)?.length ?? 0) / line.length : 0;
}
function normalizeMrz(input) {
  const warnings = [];
  const text = (Array.isArray(input) ? input.join("\n") : String(input)).toUpperCase();
  let mapped = "";
  for (const c of text) mapped += LOOKALIKES[c] ?? c;
  let lines = mapped.split(/\r?\n|\r/).map((l) => l.replace(/\s+/g, "")).filter((l) => l.length > 0);
  if (lines.length === 1) {
    const l = lines[0];
    if (l.length === 88) lines = [l.slice(0, 44), l.slice(44)];
    else if (l.length === 72) lines = [l.slice(0, 36), l.slice(36)];
    else if (l.length === 90) lines = [l.slice(0, 30), l.slice(30, 60), l.slice(60)];
  }
  if (lines.length > 3 || lines.some((l) => l.length < 25)) {
    const mrzLike = lines.filter((l) => l.length >= 25 && mrzRatio(l) >= 0.9 && l.includes("<"));
    if (mrzLike.length >= 2 && mrzLike.length < lines.length) {
      warnings.push({
        code: "extra_text_ignored",
        message: `Ignored ${lines.length - mrzLike.length} line(s) that do not look like MRZ text.`
      });
      lines = mrzLike;
    }
    if (lines.length > 3) {
      const avg = lines.slice(-3).reduce((s, l) => s + l.length, 0) / 3;
      lines = Math.abs(avg - 30) <= 3 ? lines.slice(-3) : lines.slice(-2);
      warnings.push({
        code: "extra_text_ignored",
        message: "More than 3 candidate lines; used the last lines of the text."
      });
    }
  }
  return { lines, warnings };
}
var LENGTH_TOLERANCE = 3;
function detectFormat(lines) {
  const warnings = [];
  const errors = [];
  let format = null;
  const visa = lines[0]?.startsWith("V") ?? false;
  const near = (n) => lines.every((l) => Math.abs(l.length - n) <= LENGTH_TOLERANCE);
  if (lines.length === 3 && near(30)) format = "TD1";
  else if (lines.length === 2 && near(44)) format = visa ? "MRVA" : "TD3";
  else if (lines.length === 2 && near(36)) format = visa ? "MRVB" : "TD2";
  if (!format) {
    errors.push({
      code: "unknown_format",
      message: `Could not recognize an MRZ format from ${lines.length} line(s) of length ${lines.map((l) => l.length).join(", ") || 0}. Expected 3\xD730 (TD1), 2\xD736 (TD2/MRV-B) or 2\xD744 (TD3/MRV-A).`
    });
    return {
      format: null,
      layout: null,
      lines: [...lines],
      rawLines: [...lines],
      warnings,
      errors
    };
  }
  const layout = LAYOUTS[format];
  const fixed = lines.map((l, i) => {
    if (l.length === layout.lineLength) return l;
    const excess = l.length - layout.lineLength;
    const run = /<<[<KL]*$/.exec(l);
    const adjusted = excess > 0 ? run && run[0].length - 2 >= excess ? l.slice(0, layout.lineLength) : l : l.padEnd(layout.lineLength, "<");
    if (adjusted.length !== layout.lineLength) {
      errors.push({
        code: "line_length",
        field: `line${i + 1}`,
        message: `Line ${i + 1} has ${l.length} characters; ${format} lines have ${layout.lineLength}.`
      });
      return adjusted.slice(0, layout.lineLength).padEnd(layout.lineLength, "<");
    }
    warnings.push({
      code: "line_length_adjusted",
      message: `Line ${i + 1} had ${l.length} characters; ${l.length > layout.lineLength ? "trailing fillers removed" : "padded with fillers"} to ${layout.lineLength}.`
    });
    return adjusted;
  });
  return { format, layout, lines: fixed, rawLines: [...lines], warnings, errors };
}

// src/parse.ts
var CHECK_LABEL = {
  documentNumber: "document number",
  birthDate: "date of birth",
  expiryDate: "expiry date",
  personalNumber: "personal number",
  composite: "composite"
};
function emptyFields() {
  return {
    documentCode: null,
    documentType: null,
    issuingState: null,
    issuingStateName: null,
    surname: null,
    givenNames: null,
    nameTruncated: false,
    documentNumber: null,
    nationality: null,
    nationalityName: null,
    birthDate: null,
    birthDateRaw: null,
    sex: null,
    expiryDate: null,
    expiryDateRaw: null,
    personalNumber: null,
    optionalData1: null,
    optionalData2: null
  };
}
function failure(errors, input = [], warnings = []) {
  return {
    valid: false,
    format: null,
    beta: true,
    input,
    lines: input,
    fields: emptyFields(),
    checks: [],
    corrections: [],
    ambiguities: [],
    errors,
    warnings
  };
}
function documentType(code) {
  switch (code[0]) {
    case "P":
      return "passport";
    case "I":
    case "A":
    case "C":
      return "id-card";
    case "V":
      return "visa";
    default:
      return "other";
  }
}
var EXPECTED_CODES = {
  TD3: "P",
  TD1: "IAC",
  TD2: "IACP",
  MRVA: "V",
  MRVB: "V"
};
var stripFiller = (s) => {
  const v = s.replace(/<+$/, "");
  return v.length ? v : null;
};
function referenceOf(options) {
  return toSimpleDate(options.referenceDate ?? /* @__PURE__ */ new Date()) ?? toSimpleDate(/* @__PURE__ */ new Date());
}
function parseMrz(input, options = {}) {
  try {
    return parseUnsafe(input, options);
  } catch (e) {
    return failure([
      {
        code: "internal_error",
        message: `Unexpected error: ${e instanceof Error ? e.message : String(e)}`
      }
    ]);
  }
}
function parseUnsafe(input, options) {
  if (typeof input !== "string" && !(Array.isArray(input) && input.every((l) => typeof l === "string"))) {
    return failure([
      { code: "invalid_input", message: "Input must be a string or an array of strings." }
    ]);
  }
  const normalized = normalizeMrz(input);
  if (!normalized.lines.length) {
    return failure([{ code: "no_mrz_found", message: "No MRZ text found in the input." }]);
  }
  const detection = detectFormat(normalized.lines);
  const warnings = [...normalized.warnings, ...detection.warnings];
  const errors = [...detection.errors];
  if (!detection.layout) return failure(errors, detection.lines, warnings);
  const layout = detection.layout;
  let inputLines = detection.lines;
  if (options.correct !== false && detection.rawLines.some((l) => l.length !== layout.lineLength)) {
    const repaired = repairLineLengths(
      detection.rawLines,
      detection.lines,
      layout,
      options.maxSubstitutions ?? 2
    );
    if (repaired.warnings.length) {
      inputLines = repaired.lines;
      warnings.push(...repaired.warnings);
      const fixed = new Set(repaired.repaired.map((i) => `line${i + 1}`));
      for (let i = errors.length - 1; i >= 0; i--) {
        if (errors[i].code === "line_length" && fixed.has(errors[i].field ?? ""))
          errors.splice(i, 1);
      }
    }
  }
  inputLines.forEach((l, i) => {
    const bad = [...new Set(l.replace(/[A-Z0-9<]/g, ""))];
    if (bad.length) {
      errors.push({
        code: "invalid_characters",
        message: `Line ${i + 1} contains characters outside A-Z, 0-9 and <: ${bad.join(" ")}`
      });
    }
  });
  let lines = inputLines;
  let corrections = [];
  let ambiguities = [];
  if (options.correct !== false) {
    const out = correctLines(inputLines, layout, options.maxSubstitutions ?? 2);
    lines = out.lines;
    corrections = out.corrections;
    ambiguities = out.ambiguities;
    warnings.push(...out.warnings);
    for (const c of corrections) {
      warnings.push({
        code: "corrected",
        field: c.field,
        message: `Corrected ${c.field} from "${c.original}" to "${c.corrected}" (${c.reason}).`
      });
    }
  }
  const grid = lines.map((l) => [...l]);
  const checks = [];
  const changed = new Set(
    corrections.flatMap((c) => c.changes.map((x) => `${x.line}:${x.position}`))
  );
  let longDocumentNumber = false;
  for (const check of layout.checks) {
    const rc = resolveCheck(layout, check, lines);
    if (!rc) continue;
    if (rc.longDocumentNumber) longDocumentNumber = true;
    const data = rc.covered.map((p) => grid[p[0]][p[1]]).join("");
    const printed = grid[rc.digit[0]][rc.digit[1]];
    const valid = checkPasses(grid, rc);
    checks.push({
      name: check.name,
      valid,
      computed: computeCheckDigit(data),
      printed,
      corrected: [...rc.covered, rc.digit].some((p) => changed.has(`${p[0]}:${p[1]}`))
    });
    if (!valid) {
      errors.push({
        code: "check_digit_failed",
        field: check.name,
        message: `The ${CHECK_LABEL[check.name]} check digit is "${printed}" but the data gives ${computeCheckDigit(data) ?? "no valid digit"}.`
      });
    }
  }
  const fields = extractFields(layout, lines, longDocumentNumber, options, errors, warnings);
  return {
    valid: errors.length === 0,
    format: layout.format,
    beta: true,
    input: inputLines,
    lines,
    fields,
    checks,
    corrections,
    ambiguities,
    errors,
    warnings
  };
}
function extractFields(layout, lines, longDocumentNumber, options, errors, warnings) {
  const fields = emptyFields();
  const raw = (name2) => {
    const d = fieldDef(layout, name2);
    return d ? readField(lines, d) : null;
  };
  const reference = referenceOf(options);
  const code = raw("documentCode");
  fields.documentCode = stripFiller(code);
  fields.documentType = documentType(code);
  if (!EXPECTED_CODES[layout.format].includes(code[0])) {
    warnings.push({
      code: "unexpected_document_code",
      field: "documentCode",
      message: `Document code "${code}" is unusual for ${layout.format}.`
    });
  }
  for (const key of ["issuingState", "nationality"]) {
    const value = raw(key);
    const info = lookupCountry(value);
    fields[key] = info.code || null;
    fields[key === "issuingState" ? "issuingStateName" : "nationalityName"] = info.name;
    if (info.kind === "unknown") {
      warnings.push({
        code: "unknown_country_code",
        field: key,
        message: `Unknown country code "${value}".`
      });
    }
  }
  const name = parseName(raw("name"));
  fields.surname = name.surname || null;
  fields.givenNames = name.givenNames || null;
  fields.nameTruncated = name.truncated;
  if (name.truncated) {
    warnings.push({
      code: "name_truncated",
      field: "name",
      message: "The name field is completely filled; the name was probably truncated. Check it against the visual zone."
    });
  }
  if (/[^A-Z<]/.test(raw("name"))) {
    warnings.push({
      code: "invalid_name_characters",
      field: "name",
      message: "The name contains digits or symbols."
    });
  }
  const docNumber = raw("documentNumber");
  const opt1 = raw("optionalData1");
  if (longDocumentNumber && opt1) {
    const end = opt1.indexOf("<");
    const overflow = end < 0 ? opt1 : opt1.slice(0, end);
    fields.documentNumber = docNumber + overflow.slice(0, -1);
    fields.optionalData1 = stripFiller(end < 0 ? "" : opt1.slice(end + 1));
  } else {
    fields.documentNumber = stripFiller(docNumber);
    fields.optionalData1 = opt1 === null ? null : stripFiller(opt1);
  }
  const opt2 = raw("optionalData2");
  fields.optionalData2 = opt2 === null ? null : stripFiller(opt2);
  const personal = raw("personalNumber");
  fields.personalNumber = personal === null ? null : stripFiller(personal);
  for (const key of ["birthDate", "expiryDate"]) {
    const value = raw(key);
    const parsed = parseMrzDate(
      value,
      key === "birthDate" ? "birth" : "expiry",
      reference,
      options.expiryMaxYearsAhead ?? 20
    );
    fields[key === "birthDate" ? "birthDateRaw" : "expiryDateRaw"] = value;
    fields[key] = parsed.iso;
    const label = key === "birthDate" ? "date of birth" : "expiry date";
    if (parsed.status === "invalid") {
      errors.push({
        code: "invalid_date",
        field: key,
        message: `The ${label} "${value}" is not a valid date.`
      });
    } else if (parsed.status === "missing" || parsed.status === "unknown") {
      warnings.push({
        code: parsed.status === "missing" ? "date_missing" : "date_partially_unknown",
        field: key,
        message: `The ${label} "${value}" is ${parsed.status === "missing" ? "not given" : "partially unknown"}.`
      });
    } else if (key === "expiryDate" && parsed.date && compareDates(parsed.date, reference) < 0) {
      warnings.push({
        code: "document_expired",
        field: key,
        message: `The document expired on ${parsed.iso}.`
      });
    }
  }
  const sex = raw("sex");
  if (sex === "M" || sex === "F") fields.sex = sex;
  else if (sex === "X") {
    fields.sex = "X";
    warnings.push({ code: "sex_x", field: "sex", message: 'Sex is given as "X" (unspecified).' });
  } else if (sex !== "<") {
    errors.push({
      code: "invalid_sex",
      field: "sex",
      message: `Sex "${sex}" is not M, F, X or <.`
    });
  }
  return fields;
}
export {
  DIGIT_FOR_LETTER,
  ICAO_SPECIAL_CODES,
  LAYOUTS,
  LETTERS_FOR_DIGIT,
  charValue,
  computeCheckDigit,
  detectFormat,
  isKnownCountry,
  isValidCheckDigit,
  lookupCountry,
  normalizeMrz,
  parseMrz,
  parseName
};
//# sourceMappingURL=index.js.map